import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Mail, Lock, User as UserIcon, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;
// hCaptcha site anahtarı — .env: REACT_APP_HCAPTCHA_SITE_KEY
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001';

/**
 * Giriş / Kayıt / Şifremi Unuttum — ayrı sayfa yok, ekranda tek modalda açılır.
 * Açmak için herhangi bir yerden:
 *   window.dispatchEvent(new CustomEvent('donut:auth', { detail: { tab: 'login' | 'register' | 'forgot' } }))
 *
 * AuthContext metodlarını kullanır:
 *   login(email, password) · register(email, site_username, password, captcha_token, roblox_username)
 * Şifre sıfırlama: POST /api/auth/forgot-password { email }  (reset-password ayrı sayfada kalır — token'lı link).
 */
const AuthModalContent = () => {
  const { login, register } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState('login'); // 'login' | 'register' | 'forgot'
  const [loading, setLoading] = useState(false);
  const captchaRef = useRef(null);

  // Ortak alanlar
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Kayıt alanları
  const [siteUsername, setSiteUsername] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  // Şifremi unuttum
  const [forgotSuccess, setForgotSuccess] = useState(false);


  // ---- Global event ile aç ----
  useEffect(() => {
    const onOpen = (e) => {
      const t = e?.detail?.tab;
      setTab(t === 'register' || t === 'forgot' ? t : 'login');
      setForgotSuccess(false);
      setIsOpen(true);
    };
    window.addEventListener('donut:auth', onOpen);
    return () => window.removeEventListener('donut:auth', onOpen);
  }, []);

  const resetForm = () => {
    setEmail(''); setPassword(''); setSiteUsername('');
    setCaptchaToken('');
    setForgotSuccess(false);
    try { captchaRef.current?.resetCaptcha?.(); } catch (_) {}
  };

  const close = () => { setIsOpen(false); resetForm(); };

  const switchTab = (t) => { setForgotSuccess(false); setTab(t); };

  // ---- GİRİŞ ----
  const handleLogin = async () => {
    if (!email.trim() || !password) return toast.error('Please enter your email and password');
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success('Welcome back!');
      close();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally { setLoading(false); }
  };

  // ---- KAYIT ----
  const handleRegister = async () => {
    if (!email.trim() || !siteUsername.trim() || !password) {
      return toast.error('Please fill in all required fields');
    }
    if (!/^[a-zA-Z0-9_]+$/.test(siteUsername.trim())) {
      return toast.error('Username can only contain letters, numbers, and underscores (_)');
    }
    if (password.length < 6) return toast.error('Password must be at least 6 characters long');
    if (!captchaToken) return toast.error('Please complete the captcha');

    setLoading(true);
    try {
      await register(
        email.trim().toLowerCase(),
        siteUsername.trim(),
        password,
        captchaToken
      );
      toast.success('Account created — welcome!');
      close();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Registration failed';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
      toast.error(msg);
      try { captchaRef.current?.resetCaptcha?.(); } catch (_) {}
      setCaptchaToken('');
    } finally { setLoading(false); }
  };

  // ---- ŞİFREMİ UNUTTUM ----
  const handleForgot = async () => {
    if (!email.trim()) return toast.error('Please enter your email');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setForgotSuccess(true);
      toast.success(res.data?.message || 'Reset link sent');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong.');
    } finally { setLoading(false); }
  };

  const submit = () => {
    if (tab === 'login') return handleLogin();
    if (tab === 'register') return handleRegister();
    return handleForgot();
  };

  const c = tab === 'register'
    ? { grad: 'from-[#7b2fc4] to-[#B059E8] hover:from-[#B059E8] hover:to-[#c98bf0]', focus: 'focus:border-[#B059E8]' }
    : { grad: 'from-[#15913f] to-[#16A34A] hover:from-[#16A34A] hover:to-[#2ecf6e]', focus: 'focus:border-[#16A34A]' };

  // Sol banner rozeti — sadece bilgi tasidigi sekmelerde gosterilir.
  // Login sekmesinde rozet YOK (dolgu metni gorunuyordu).
  const bannerBadge =
    tab === 'register'
      ? { text: 'Free welcome bonus', cls: 'text-[#e9cffa] bg-[#B059E8]/25 border-[#B059E8]/50' }
      : tab === 'forgot'
        ? { text: 'Account recovery', cls: 'text-white/90 bg-white/15 border-white/30' }
        : null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={close}>
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="w-full max-w-3xl bg-[#1A1D20] rounded-2xl border border-[#2A2D31] shadow-2xl relative my-4 md:my-8 flex overflow-hidden md:h-[600px] max-h-[92vh]"
            onClick={e => e.stopPropagation()}
          >
            {/* ═══ SOL BANNER — reklam alanı (mobilde gizli). public/auth-banner.png koy; yoksa aşağıdaki fallback görünür. ═══ */}
            <div className="hidden md:block relative w-[280px] shrink-0 overflow-hidden bg-gradient-to-br from-[#7b2fc4] via-[#3a1d5c] to-[#0f2e1c]">
              <img
                src="/auth-banner.png"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />

              {/* ── OKUNABİLİRLİK KATMANLARI ──────────────────────────────
                  Yazılar görselin renklerine karışmasın diye üç ayrı katman.
                  Düz siyah bir örtü (bg-black/50) fotoğrafı öldürürdü; bunun
                  yerine sadece yazının olduğu bölgeleri karartıyoruz.        */}

              {/* 1) Yönlü scrim: alttan yukarı açılır — görselin üst yarısı temiz kalır */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(to top, rgba(8,5,16,0.78) 0%, rgba(8,5,16,0.52) 18%, rgba(8,5,16,0.22) 38%, rgba(8,5,16,0.04) 60%, rgba(8,5,16,0) 100%)',
                }}
              />
              {/* 2) Üstten kısa scrim: logonun arkasını sabitler */}
              <div
                className="absolute inset-x-0 top-0 h-24 pointer-events-none"
                style={{ background: 'linear-gradient(to bottom, rgba(8,5,16,0.52) 0%, rgba(8,5,16,0) 100%)' }}
              />
              {/* 3) Vignette: kenarlardan içeri karartma — hareketli görsellerde metni tutar */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: 'inset 0 0 70px 8px rgba(8,5,16,0.28)' }}
              />

              {/* İçerik */}
              <div className="relative z-[1] h-full flex flex-col justify-between p-6">
                <div className="flex items-center gap-2">
                  <img
                    src="/logo.png"
                    alt=""
                    className="w-7 h-7 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.65)]"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span
                    className="font-heading font-black text-lg text-white tracking-widest"
                    style={{ textShadow: '0 1px 4px rgba(0,0,0,0.85), 0 0 10px rgba(0,0,0,0.45)' }}
                  >
                    BLOX<span className="text-[#c98bf0]">BETS</span>
                  </span>
                </div>

                <div>
                  {bannerBadge && (
                    <span
                      className={`inline-block mb-3 px-2.5 py-1 rounded-full border backdrop-blur-sm text-[10px] font-bold uppercase tracking-[0.12em] ${bannerBadge.cls}`}
                    >
                      {bannerBadge.text}
                    </span>
                  )}
                  <h3
                    className="font-heading font-black text-2xl text-white leading-tight mb-2"
                    style={{ textShadow: '0 2px 8px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.95)' }}
                  >
                    The fairest<br />social casino
                  </h3>
                  {/* text-white/70 -> /90: opaklıkla hiyerarşi kurmak okunabilirliği düşürüyordu.
                      Hiyerarşi artık boyut + ağırlık farkıyla sağlanıyor. */}
                  <p
                    className="text-white/90 text-sm leading-relaxed"
                    style={{ textShadow: '0 1px 5px rgba(0,0,0,0.9), 0 0 14px rgba(0,0,0,0.55)' }}
                  >
                    Provably fair games, just for fun. Sign up and grab your free welcome bonus.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══ SAĞ: form sütunu ═══ */}
            <div className="flex-1 w-full flex flex-col min-w-0 overflow-y-auto">
            {/* HEADER: logo + tab'lar (aynı satır) + kapat — ayrı tab satırı yok, kompakt */}
            <div className="flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-3 border-b border-[#2A2D31]">
              {/* logo */}
              <div className="flex items-center gap-1.5 shrink-0">
                <img src="/logo.png" alt="BloxBets" className="w-6 h-6 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                <h2 className="font-heading font-black text-base sm:text-lg text-white tracking-widest">BLOX<span className="text-[#B059E8]">BETS</span></h2>
              </div>

              {/* tab'lar (login/register) — forgot modunda geri + başlık */}
              {tab !== 'forgot' ? (
                <div className="flex gap-1.5 flex-1 min-w-0">
                  {['login', 'register'].map((t) => (
                    <button
                      key={t}
                      onClick={() => switchTab(t)}
                      className={`flex-1 px-2 py-2 rounded-lg font-bold text-xs sm:text-sm transition-all ${
                        tab === t
                          ? (t === 'login' ? 'bg-[#16A34A] text-white' : 'bg-[#B059E8] text-white')
                          : 'bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      {t === 'login' ? 'Login' : 'Register'}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button onClick={() => switchTab('login')} className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors shrink-0">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <span className="font-bold text-sm text-white truncate">Reset Password</span>
                </div>
              )}

              {/* kapat */}
              <button onClick={close} className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3">

              {/* ══════════ ŞİFREMİ UNUTTUM ══════════ */}
              {tab === 'forgot' ? (
                forgotSuccess ? (
                  <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[#16A34A]/10 border border-[#16A34A]/25 p-6 rounded-xl text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#2ecf6e] mx-auto mb-3" />
                    <h3 className="text-[#2ecf6e] font-bold mb-1">Email Sent!</h3>
                    <p className="text-[#86efac]/70 text-sm">Check your inbox and spam folder for the reset link.</p>
                    <button onClick={() => switchTab('login')} className="mt-5 w-full py-3 bg-white/5 hover:bg-white/10 rounded-xl text-zinc-300 hover:text-white font-bold transition-colors">
                      Back to Login
                    </button>
                  </motion.div>
                ) : (
                  <>
                    <p className="text-sm text-zinc-400 text-center">Enter your email address to receive a reset link.</p>
                    <div className="space-y-2">
                      <label className="text-sm text-zinc-400">Email</label>
                      <div className="relative">
                        <Mail className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="email" value={email} onChange={e => setEmail(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && submit()}
                          placeholder="you@example.com"
                          className="w-full p-2.5 pl-10 bg-[#15171a] border border-[#2A2D31] rounded-xl text-white outline-none focus:border-[#B059E8] transition-colors"
                        />
                      </div>
                    </div>
                    <button
                      onClick={submit} disabled={loading}
                      className="w-full py-3 mt-1 bg-gradient-to-r from-[#7b2fc4] to-[#B059E8] hover:from-[#B059E8] hover:to-[#c98bf0] rounded-xl text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? <Loader2 className="animate-spin w-5 h-5" /> : 'Send Reset Link'}
                    </button>
                  </>
                )
              ) : (
                <>
                  {/* EMAIL */}
                  <div className="space-y-2">
                    <label className="text-sm text-zinc-400">Email</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="email" value={email} onChange={e => setEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && submit()}
                        placeholder="you@example.com"
                        className={`w-full p-2.5 pl-10 bg-[#15171a] border border-[#2A2D31] rounded-xl text-white outline-none ${c.focus} transition-colors`}
                      />
                    </div>
                  </div>

                  {/* SITE USERNAME (sadece kayıt) */}
                  {tab === 'register' && (
                    <div className="space-y-2">
                      <label className="text-sm text-zinc-400">Username</label>
                      <div className="relative">
                        <UserIcon className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text" value={siteUsername} onChange={e => setSiteUsername(e.target.value)}
                          placeholder="3-16 chars · letters, numbers, _"
                          className={`w-full p-2.5 pl-10 bg-[#15171a] border border-[#2A2D31] rounded-xl text-white outline-none ${c.focus} transition-colors`}
                        />
                      </div>
                    </div>
                  )}

                  {/* PASSWORD */}
                  <div className="space-y-2">
                    <label className="text-sm text-zinc-400">Password</label>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="password" value={password} onChange={e => setPassword(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && submit()}
                        placeholder={tab === 'register' ? 'At least 6 characters' : '••••••••'}
                        className={`w-full p-2.5 pl-10 bg-[#15171a] border border-[#2A2D31] rounded-xl text-white outline-none ${c.focus} transition-colors`}
                      />
                    </div>
                    {tab === 'login' && (
                      <div className="text-right">
                        <button onClick={() => switchTab('forgot')} className="text-xs text-zinc-500 hover:text-[#c98bf0] transition-colors">
                          Forgot password?
                        </button>
                      </div>
                    )}
                  </div>

                  {/* CAPTCHA (sadece kayıt) */}
                  {tab === 'register' && (
                    <div className="flex justify-center">
                      <HCaptcha
                        ref={captchaRef}
                        sitekey={HCAPTCHA_SITE_KEY}
                        theme="dark"
                        onVerify={(t) => setCaptchaToken(t)}
                        onExpire={() => setCaptchaToken('')}
                        onError={() => setCaptchaToken('')}
                      />
                    </div>
                  )}

                  {/* SUBMIT */}
                  <button
                    onClick={submit}
                    disabled={loading}
                    className={`w-full py-3 mt-1 bg-gradient-to-r ${c.grad} rounded-xl text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                  >
                    {loading ? <Loader2 className="animate-spin w-5 h-5" /> : (tab === 'login' ? 'Login' : 'Create Account')}
                  </button>

                  {/* ALT bilgi */}
                  <p className="text-center text-xs text-zinc-500">
                    {tab === 'login' ? (
                      <>No account?{' '}
                        <button onClick={() => switchTab('register')} className="text-[#c98bf0] hover:text-[#d8b8f0] font-bold">Register</button>
                      </>
                    ) : (
                      <>Already have an account?{' '}
                        <button onClick={() => switchTab('login')} className="text-[#2ecf6e] hover:text-[#5ee08f] font-bold">Login</button>
                      </>
                    )}
                  </p>
                </>
              )}
            </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export const AuthModal = () => {
  if (typeof document !== 'undefined') {
    return ReactDOM.createPortal(<AuthModalContent />, document.body);
  }
  return null;
};

export default AuthModal;